import React, { ReactNode } from 'react'
import { useAccount, useNetwork } from 'wagmi'
import { parseUnits } from 'ethers/lib/utils.js'
import { CircularProgress } from '@mui/material'
import { toast } from 'react-toastify'

import { useRouter } from 'next/router'
import { BigNumber } from 'ethers'

import { useModal } from '@ebay/nice-modal-react'
import { StyledButton, Text1 } from '../Left/styleds'

import Brc20FeeDialog from '.'
import { useAllowance, useApprove } from '@/hooks/brc20Hooks'
import { getBrc20ContractAddress, getFactoryContractAddress, getVaultContractAddress } from '@/utils/contract'
import { SupportedTicker, USDT_TICKER } from '@/constants/token'
import { useWallet } from '@/components/common/WalletContext'
import { getTokenDecimals, shouldUseValutContract } from '@/utils'
import useFee from '@/hooks/useFee'
import useDeposit from '@/hooks/useDeposit'
import useBalanceOfNativeToken from '@/hooks/useBalanceOfNativeToken'

interface MainButtonProps {
  withdrawAmount: string
  receiverAddress: string
  tickerErcName: string
  tokenName: SupportedTicker
  brc20Balance?: BigNumber
}

interface ButtonProps {
  onClick?: () => void
  children?: ReactNode
  disabled?: boolean
  loading?: boolean
}

const Button = ({ onClick, children, disabled, loading }: ButtonProps) => {
  return (
    <StyledButton sx={{ mt: { xs: 20, md: 32 } }} fullWidth onClick={onClick} disabled={disabled}>
      {loading ? <CircularProgress sx={{ color: '#fff' }} size={20} /> : <Text1>{children}</Text1>}
    </StyledButton>
  )
}

const FeeButton = ({ withdrawAmount, receiverAddress, tokenName, brc20Balance, tickerErcName }: MainButtonProps) => {
  const modal = useModal(Brc20FeeDialog)

  const { connectEvmWallet } = useWallet()
  const router = useRouter()

  const { address: account, isConnected } = useAccount()
  const { chain } = useNetwork()

  const withdrawAmountInWei =
    chain?.id && withdrawAmount ? parseUnits(withdrawAmount, getTokenDecimals(tokenName, chain?.id)) : 0

  const tokenAddress = getBrc20ContractAddress(tokenName, chain?.id)

  const factoryAddress = getFactoryContractAddress(chain?.id)
  const vaultAddress = getVaultContractAddress(chain?.id)

  const spender = shouldUseValutContract(tokenName) ? vaultAddress : factoryAddress

  const fee = useFee(tokenName)

  const { data: allowance, run: getAllowance } = useAllowance(tokenAddress, spender)

  const { data: balance } = useBalanceOfNativeToken(account)

  const { runAsync: approveAsync, loading: isApproving } = useApprove(tokenAddress, {
    onSuccess: () => {
      getAllowance()
    },
  })

  const { run: deposit, loading: isDepositing } = useDeposit(tokenName, {
    onSuccess: () => {
      getAllowance()
      toast.success('Transaction Submitted')
      modal.hide()
      setTimeout(() => {
        modal.hide()
        router.push('/account')
      }, 3000)
    },
  })

  if (!isConnected) {
    return (
      <Button
        onClick={() => {
          connectEvmWallet()
        }}
      >
        Connect Wallet
      </Button>
    )
  }

  if (balance && balance?.lt(fee?.toString())) {
    return <Button disabled>Insufficient {chain?.nativeCurrency.symbol} Balance</Button>
  }

  if (brc20Balance?.lt(withdrawAmountInWei)) {
    return <Button disabled>Insufficient {tickerErcName} Balance</Button>
  }

  if (allowance?.lt(withdrawAmountInWei)) {
    return (
      <Button
        loading={isApproving}
        disabled={isApproving}
        onClick={async () => {
          if (tokenName === USDT_TICKER.name && allowance?.gt(0)) {
            await approveAsync(spender, 0)
          }

          approveAsync(spender, withdrawAmountInWei)
        }}
      >
        Approve
      </Button>
    )
  }

  return (
    <Button
      loading={isDepositing}
      disabled={isDepositing}
      onClick={() => {
        const params = { amount: withdrawAmountInWei, tokenAddress: tokenAddress, receiverAddress: receiverAddress }
        deposit(params)
      }}
    >
      Transfer
    </Button>
  )
}

export default FeeButton
